import React, { useEffect, useRef } from "react";
import { APIProvider } from "@vis.gl/react-google-maps";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { Provider, useSelector } from "react-redux";
import {
  Link,
  Navigate,
  Route,
  Routes,
  BrowserRouter as Router,
  useLocation,
} from "react-router-dom";
import "./App.css";
import Login from "./components/auth/Login";
import Alert from "./components/layout/Alert";
import MapResults from "./components/layout/MapResults";
import greenv from "./components/layout/Vegifyit-Logo-RGB-LR.png";
import AdminMainPage from "./components/layout/administration/AdminMainPage";
import CreateAccount from "./components/layout/administration/CreateAccount";
import PasswordReset from "./components/layout/administration/PasswordReset";
import SignupPage from "./components/layout/administration/SignupPage";
import SimplePage, {
  ABOUT_PAGE,
  CONTACT_PAGE,
} from "./components/layout/administration/SimplePage";
import ConsultingSection from "./components/layout/copy/Consulting";
import EventsListingPage from "./components/layout/copy/EventsListingPage";
import OrganizingFestivalPage from "./components/layout/copy/OrganizingFestivalPage";
import CityMapPage from "./components/layout/CityMapPage";
import Explore from "./components/layout/explore/Explore";
import ListPage from "./components/layout/ListPage";
import Actions from "./components/layout/navBar/Actions";
import Footer from "./components/layout/navBar/Footer";
import EmbedFooter from "./components/layout/navBar/EmbedFooter";
import FullscreenButton from "./components/layout/FullscreenButton";
import RestaurantCuisine from "./components/layout/restaurantBuilder/restaurantBuilder2/RestaurantCuisine";
import RestaurantMenuManual from "./components/layout/restaurantBuilder/restaurantBuilder2/RestaurantMenuManual";
import RestaurantNameAddressLoc from "./components/layout/restaurantBuilder/restaurantBuilder2/RestaurantNameAddressLoc";
import ProcessingPage from "./components/layout/ProcessingPage";
import RestaurantCreateBuild from "./components/layout/restaurantBuilder/restaurantBuilderLegacy/RestaurantCreateBuild";
import RestaurantCreateMatch from "./components/layout/restaurantBuilder/restaurantBuilderLegacy/RestaurantCreateMatch";
import RestaurantCreateSearch from "./components/layout/restaurantBuilder/restaurantBuilderLegacy/RestaurantCreateSearch";
import RestaurantDetails from "./components/layout/restaurantDetails/RestaurantDetails";
import RestaurantDetailsRedirect from "./components/layout/restaurantDetails/RestaurantDetailsRedirect";
import ResultSideBody from "./components/layout/restaurantResults/ResultSideBody";
import store from "./store";
import { backendLog } from "./util/logging";
import { EventProvider } from "./contexts/EventContext";
import { CityProvider } from "./contexts/CityContext";
import { SidebarProvider } from "./contexts/SidebarContext";
import EventEditor from "./components/layout/administration/EventEditor";
import EventPage from "./components/layout/EventPage";
import PageViewTracker from "./components/analytics/PageViewTracker";
import { logPageView } from "./util/analytics";
import AdHocSearchConnection from "./components/layout/AdHocSearchConnection";
import useLastMapUrl, { useHasMapHistory } from "./hooks/useLastMapUrl";
import { getLogoUrl } from "./util/navUrl";
import AdHocRestaurantDetails from "./components/layout/AdHocRestaurantDetails";
import NearbyEventToast from "./components/layout/NearbyEventToast";
// Component to manage embed-mode body class
// Uses useLocation but only reacts to pathname changes, not query params
const EmbedModeBodyClass = () => {
  const location = useLocation();
  const isEmbedMode = React.useMemo(
    () => location.pathname.includes("/embed"),
    [location.pathname] // Only recalculate when pathname changes
  );

  useEffect(function syncEmbedModeBodyClass() {
    if (isEmbedMode) {
      document.body.classList.add("embed-mode");
    } else {
      document.body.classList.remove("embed-mode");
    }
    return () => {
      document.body.classList.remove("embed-mode");
    };
  }, [isEmbedMode]); // Only run when isEmbedMode changes (which is only when pathname changes)

  return null;
};

// Navbar component that checks embed mode independently
const Navbar = () => {
  const location = useLocation();
  const isEmbedMode = location.pathname.includes("/embed");
  const lastMapUrl = useLastMapUrl();
  const hasMapHistory = useHasMapHistory();
  const restaurants = useSelector((state) => state.restaurant.restaurants);

  if (isEmbedMode) {
    return null;
  }

  const logoUrl = getLogoUrl({
    pathname: location.pathname,
    lastMapUrl,
    hasMapHistory,
    restaurants,
  });

  return (
    <div className="container1">
      <div className="navbar-brand navbar">
        <Link to={logoUrl}>
          <img
            className="d-inline-block mr-1"
            alt="vegify brand"
            src={greenv}
          />
        </Link>
        <Actions />
      </div>
    </div>
  );
};

// Footer selector component that checks embed mode independently
const FooterSelector = () => {
  const location = useLocation();
  const isEmbedMode = location.pathname.includes("/embed");

  return isEmbedMode ? <EmbedFooter /> : <Footer />;
};

const AppContent = (props) => {
  useEffect(function logVisitorOnRender() {
    backendLog("New visitor!"); // Keep for Elastic
    logPageView(); // Add for analytics
  });

  return (
    <>
      <EmbedModeBodyClass />
      <FullscreenButton />
      <Navbar />
      <Alert />
      <NearbyEventToast />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/about" element={<SimplePage content={ABOUT_PAGE} />} />
        <Route path="/contact" element={<SimplePage content={CONTACT_PAGE} />} />
        <Route path="/consulting" element={<ConsultingSection />} />
        <Route path="/events" element={<EventsListingPage />} />
        <Route path="/organizing-a-festival" element={<OrganizingFestivalPage />} />
        <Route path="/events/:eventSlug" element={<EventPage />} />
        <Route path="/events/:eventSlug/embed" element={<EventPage />} />
        <Route path="/admin/events/:slug" element={<EventEditor />} />
        <Route path="/create/search" element={<RestaurantCreateSearch />} />
        <Route path="/create/match" element={<RestaurantCreateMatch />} />
        <Route path="/create/build" element={<RestaurantCreateBuild />} />
        <Route path="/create/processing/:jobId" element={<ProcessingPage />} />
        <Route path="/create/v2/address" element={<RestaurantNameAddressLoc />} />
        <Route path="/create/v2/cuisine" element={<RestaurantCuisine />} />
        <Route path="/create/v2/cuisine/manual" element={<RestaurantMenuManual />} />
        <Route path="/create/v2/address/google" element={<RestaurantCreateSearch />} />
        <Route path="/ad-hoc/details" element={<AdHocRestaurantDetails />} />
        <Route path="/details/:id/:slug" element={<RestaurantDetails />} />
        <Route path="/details/:id" element={<RestaurantDetailsRedirect />} />
        <Route path="/profile" element={<AdminMainPage />} />
        <Route path="/admin" element={<AdminMainPage />} />
        <Route path="/analytics" element={<Navigate to="/profile#4" replace />} />
        <Route path="/explore" element={<Explore />} />
        <Route path="/lists/:slug" element={<ListPage />} />
        <Route path="/lists/:slug/embed" element={<ListPage />} />
        <Route path="/create-account/:secretCode" element={<CreateAccount />} />
        <Route path="/reset-password/:secretCode" element={<PasswordReset />} />
        <Route path="/cities/:state/:city" element={<CityMapPage />} />
        <Route
          path="/"
          element={
            <div className="main-app">
              <MapResults isMarkerShown />
              <ResultSideBody />
            </div>
          }
        />
      </Routes>
      <FooterSelector />
    </>
  );
};

const App = (props) => {
  return (
    <APIProvider apiKey="AIzaSyCHTvWtg42OxUZ6oyN0YeM0Oyy8oCRHFxs" libraries={["places"]}>
    <HelmetProvider>
    <Provider store={store}>
      <div className="App">
        <Helmet>
          <title>VegifyIt - Discover Vegan Food Nearby</title>
        </Helmet>
        <Router>
          <EventProvider>
            <CityProvider>
              <SidebarProvider>
                <AdHocSearchConnection />
                <PageViewTracker />
                <AppContent />
              </SidebarProvider>
            </CityProvider>
          </EventProvider>
        </Router>
      </div>
    </Provider>
    </HelmetProvider>
    </APIProvider>
  );
};

export default App;
